import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import Cookies from 'js-cookie';
import { API_BASE_URL } from '../config';

// Async thunk for register
export const registerUser = createAsyncThunk(
  'auth/registerUser',
  async (userData, { rejectWithValue }) => {
    try {
      const response = await fetch(`${API_BASE_URL}/auth/register`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(userData)
      });
      const data = await response.json();
      
      if (!response.ok) {
        return rejectWithValue(data.message || 'Registration failed');
      }

      return data;
    } catch (err) {
      return rejectWithValue('Network error connecting to server');
    }
  }
);

// Async thunk for login
export const loginUser = createAsyncThunk(
  'auth/loginUser',
  async ({ username, password }, { rejectWithValue }) => {
    try {
      const response = await fetch(`${API_BASE_URL}/auth/login`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ username, password })
      });
      const data = await response.json();
      
      if (!response.ok) {
        return rejectWithValue(data.message || 'Authentication failed');
      }

      // Store in cookies and localStorage
      Cookies.set('adminToken', data.token, { expires: 7 }); // Expires in 7 days
      Cookies.set('adminId', data.adminId, { expires: 7 });
      localStorage.setItem('adminToken', data.token);
      localStorage.setItem('adminId', data.adminId);
      Cookies.set('adminUsername', data.username, { expires: 7 });
      if (data.adminName) Cookies.set('adminName', data.adminName, { expires: 7 });
      Cookies.set('role', data.role, { expires: 7 });
      Cookies.set('licenseLimit', data.licenseLimit, { expires: 7 });
      Cookies.set('subscriptionExpiry', data.subscriptionExpiry, { expires: 7 });

      return data;
    } catch (err) {
      return rejectWithValue('Network error connecting to server');
    }
  }
);

const initialState = {
  token: Cookies.get('adminToken') || null,
  user: {
    adminId: Cookies.get('adminId') || null,
    username: Cookies.get('adminUsername') || null,
    role: Cookies.get('role') || null,
  },
  loading: false,
  error: null,
  success: false,
};

const authSlice = createSlice({
  name: 'auth',
  initialState,
  reducers: {
    logout: (state) => {
      Cookies.remove('adminToken');
      Cookies.remove('adminId');
      Cookies.remove('adminUsername');
      Cookies.remove('adminName');
      Cookies.remove('role');
      Cookies.remove('licenseLimit');
      Cookies.remove('subscriptionExpiry');
      state.token = null;
      state.user = { adminId: null, username: null, role: null };
      state.success = false;
      state.error = null;
    },
    clearError: (state) => {
      state.error = null;
    }
  },
  extraReducers: (builder) => {
    builder
      .addCase(loginUser.pending, (state) => {
        state.loading = true;
        state.error = null;
        state.success = false;
      })
      .addCase(loginUser.fulfilled, (state, action) => {
        state.loading = false;
        state.success = true;
        state.token = action.payload.token;
        state.user = {
          adminId: action.payload.adminId,
          username: action.payload.username,
          role: action.payload.role,
        };
      })
      .addCase(loginUser.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
        state.success = false;
      })
      // Register cases
      .addCase(registerUser.pending, (state) => {
        state.loading = true;
        state.error = null;
        state.success = false;
      })
      .addCase(registerUser.fulfilled, (state) => {
        state.loading = false;
        state.success = true;
      })
      .addCase(registerUser.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
        state.success = false;
      });
  }
});

export const { logout, clearError } = authSlice.actions;
export default authSlice.reducer;
